import React, { useState, useEffect, useRef } from 'react';
import { Search, X, Tags, ChevronDown, Trash } from 'lucide-react';

const FiltersSection = ({
    filter,
    onFilterChange,
    candidatesPerPage,
    candidatesLength,
    isDeletingAll,
    isDeletingRejected, // NEW: Add this prop
    onDeleteAllClick,
    activeView,
    onAdvancedFilterChange,
    advancedFilters = {}
}) => {
    const [showFilterModal, setShowFilterModal] = useState(false);
    const [keywordInput, setKeywordInput] = useState('');
    const [activeKeywords, setActiveKeywords] = useState([]);
    const [filterOptions, setFilterOptions] = useState({
        skills: [],
        notice_periods: [],
        locations: [],
        companies: []
    });
    const inputRef = useRef(null);

    // Initialize keywords from props
    useEffect(() => {
        const allKeywords = [
            ...(advancedFilters.skills || []),
            ...(advancedFilters.locations || []),
            ...(advancedFilters.companies || []),
            ...(advancedFilters.notice_periods || [])
        ];
        setActiveKeywords(allKeywords);
    }, [advancedFilters]);

    // Load filter options
    const loadFilterOptions = async () => {
        try {
            const token = localStorage.getItem('adminToken');
            const response = await fetch('https://n8n.acespireconsulting.com/webhook/filter-options', {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                if (data.success && data.filter_options) {
                    setFilterOptions(data.filter_options);
                }
            }
        } catch (error) {
            console.error('Failed to load filter options:', error);
        }
    };

    // Categorize keywords automatically
    const categorizeKeyword = (keyword) => {
        const lowerKeyword = keyword.toLowerCase().trim();
        
        // Notice period patterns
        const noticePeriodPatterns = ['immediate', 'asap', 'days', 'day', 'weeks', 'week', 'months', 'month'];
        
        // Location patterns (common Indian cities)
        const locationPatterns = [
            'bangalore', 'mumbai', 'delhi', 'chennai', 'hyderabad', 'pune', 'kolkata', 
            'ahmedabad', 'surat', 'jaipur', 'lucknow', 'kanpur', 'nagpur', 'indore'
        ];
        
        // Company patterns (common companies)
        const companyPatterns = [
            'tcs', 'infosys', 'wipro', 'accenture', 'cognizant', 'hcl', 'tech mahindra',
            'google', 'microsoft', 'amazon', 'apple', 'meta', 'facebook', 'acespire'
        ];
        
        // Check notice periods first
        if (noticePeriodPatterns.some(pattern => lowerKeyword.includes(pattern)) ||
            /^\d+\s*(day|days|week|weeks|month|months)$/.test(lowerKeyword)) {
            return 'notice_periods';
        }
        
        // Check locations
        if (locationPatterns.some(location => lowerKeyword.includes(location))) {
            return 'locations';
        }
        
        // Check companies
        if (companyPatterns.some(company => lowerKeyword.includes(company))) {
            return 'companies';
        }
        
        // Default to skills
        return 'skills';
    };

    // Add keyword with proper categorization
    const addKeyword = (keyword) => {
        const trimmedKeyword = keyword.trim();
        if (trimmedKeyword && !activeKeywords.includes(trimmedKeyword)) {
            const newKeywords = [...activeKeywords, trimmedKeyword];
            setActiveKeywords(newKeywords);
            
            // Categorize all keywords properly
            const categorizedFilters = {
                skills: [],
                locations: [],
                companies: [],
                notice_periods: []
            };
            
            newKeywords.forEach(kw => {
                const category = categorizeKeyword(kw);
                categorizedFilters[category].push(kw);
            });
            
            onAdvancedFilterChange(categorizedFilters);
        }
        setKeywordInput('');
        inputRef.current?.focus();
    };

    // Remove keyword
    const removeKeyword = (keywordToRemove) => {
        const newKeywords = activeKeywords.filter(keyword => keyword !== keywordToRemove);
        setActiveKeywords(newKeywords);
        
        // Re-categorize remaining keywords
        const categorizedFilters = {
            skills: [],
            locations: [],
            companies: [],
            notice_periods: []
        };
        
        newKeywords.forEach(kw => {
            const category = categorizeKeyword(kw);
            categorizedFilters[category].push(kw);
        });
        
        onAdvancedFilterChange(categorizedFilters);
    };

    // Handle key press
    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (keywordInput.trim()) {
                addKeyword(keywordInput);
            }
        }
    };

    // Clear all keywords
    const clearAllKeywords = () => {
        setActiveKeywords([]);
        onAdvancedFilterChange({
            skills: [],
            locations: [],
            companies: [],
            notice_periods: []
        });
        setKeywordInput('');
    };

    // Open modal and load filter options
    const openFilterModal = () => {
        setShowFilterModal(true);
        loadFilterOptions();
    };

    // Get popular suggestions
    const getPopularSuggestions = () => {
        const allSuggestions = [
            ...(filterOptions.skills || []).slice(0, 5),
            ...(filterOptions.locations || []).slice(0, 3),
            ...(filterOptions.companies || []).slice(0, 2),
            ...(filterOptions.notice_periods || []).slice(0, 3)
        ];
        return allSuggestions.filter(item => !activeKeywords.includes(item.value));
    };

    // Check if any delete operation is in progress
    const isDeleting = isDeletingAll || isDeletingRejected;

    // Navigation handlers
    const handleUploadResume = () => {
        window.location.href = '/';
    };

    const handleCreateJob = () => {
        window.location.href = '/create-job';
    };

    return (
        <>
            {/* Main Filter Bar */}
            <div className="bg-white m-2 rounded-lg px-6 py-3 border-b border-gray-200 flex-shrink-0">
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-4">
                        <h3 className="text-sm font-medium text-gray-900">Filters</h3>
                        <span className="text-xs text-gray-500">
                            {candidatesPerPage} candidates per page
                        </span>
                        {activeKeywords.length > 0 && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                <Tags className="w-3 h-3 mr-1" />
                                {activeKeywords.length} keyword{activeKeywords.length > 1 ? 's' : ''}
                            </span>
                        )}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center gap-3">
                        <button
                            onClick={handleUploadResume}
                            className="px-4 py-2 text-sm font-medium text-white bg-[#275F97] hover:bg-[#1e4a7a] rounded-lg transition-colors"
                        >
                            Upload Resume
                        </button>
                        
                        <button
                            onClick={handleCreateJob}
                            className="px-4 py-2 text-sm font-medium text-white bg-[#275F97] hover:bg-[#1e4a7a] rounded-lg transition-colors"
                        >
                            Create Job
                        </button>
                    </div>

                </div>
                
                {/* Status Filters */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-6"> 
                        <label className="flex items-center cursor-pointer">
                            <input
                                type="checkbox"
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2"
                                checked={filter.shortlisted}
                                onChange={() => onFilterChange('shortlisted')}
                            />
                            <span className="text-sm text-gray-700">Shortlisted</span>
                        </label>
                        
                        <label className="flex items-center cursor-pointer">
                            <input
                                type="checkbox"
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2"
                                checked={filter.rejected}
                                onChange={() => onFilterChange('rejected')}
                            />
                            <span className="text-sm text-gray-700">Rejected</span>
                        </label>
                        
                        <label className="flex items-center cursor-pointer">
                            <input
                                type="checkbox"
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2"
                                checked={filter.waitlist}
                                onChange={() => onFilterChange('waitlist')}
                            />
                            <span className="text-sm text-gray-700">Under Review</span>
                        </label>
                    </div>

                    {/* Delete and Add Keywords Buttons */}
                    <div className="flex items-center gap-3">
                        <button
                            onClick={onDeleteAllClick}
                            disabled={candidatesLength === 0 || isDeleting}
                            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                                candidatesLength === 0 || isDeleting
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    : 'bg-red-50 text-red-700 hover:bg-red-100 border '
                            }`}
                        >
                            {isDeleting ? 'Deleting...' : 'Delete'}
                            <Trash className={`w-4 h-4 ${isDeleting ? 'animate-spin' : ''}`} />
                        </button>

                        <button
                            onClick={openFilterModal}
                            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-[#275F97] hover:text-blue-800 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                        >
                            <Search className="w-4 h-4" />
                            <span>Add Keywords</span>
                            {activeKeywords.length > 0 && (
                                <span className="bg-[#275F97] text-white text-xs px-2 py-1 rounded-full">
                                    {activeKeywords.length}
                                </span>
                            )}
                        </button>
                    </div>
                </div>

                {/* Active Keywords Display */}
                {activeKeywords.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-gray-700">Active Keywords:</span>
                            <button
                                onClick={clearAllKeywords}
                                className="text-sm text-red-600 hover:text-red-800 underline"
                            >
                                Clear all
                            </button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {activeKeywords.map((keyword, index) => (
                                <span
                                    key={index}
                                    className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                                >
                                    {keyword}
                                    <button
                                        onClick={() => removeKeyword(keyword)}
                                        className="ml-2 hover:text-blue-600"
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                </span>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Filter Modal */}
            {showFilterModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowFilterModal(false)}>
                    <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-lg font-semibold text-gray-900">Add Filter Keywords</h3>
                            <button
                                onClick={() => setShowFilterModal(false)}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        {/* Keyword Input */}
                        <div className="mb-6">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Type keywords and press Enter
                            </label>
                            <input
                                ref={inputRef}
                                type="text"
                                value={keywordInput}
                                onChange={(e) => setKeywordInput(e.target.value)}
                                onKeyPress={handleKeyPress}
                                placeholder="e.g., Python, Mumbai, 30 days, Google..."
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
                                autoFocus
                            />
                            <p className="text-sm text-gray-500 mt-2">
                                Keywords are automatically categorized as skills, locations, companies, or notice periods.
                            </p>
                        </div>

                        {/* Popular Suggestions */}
                        {getPopularSuggestions().length > 0 && (
                            <div className="mb-6">
                                <h4 className="text-sm font-medium text-gray-700 mb-3">Popular Keywords:</h4>
                                <div className="flex flex-wrap gap-2">
                                    {getPopularSuggestions().slice(0, 12).map((suggestion, index) => (
                                        <button
                                            key={index}
                                            onClick={() => addKeyword(suggestion.value)}
                                            className="px-3 py-2 text-sm bg-gray-100 hover:bg-blue-100 text-gray-700 hover:text-blue-700 rounded-lg transition-colors"
                                        >
                                            {suggestion.value}
                                            {suggestion.count && (
                                                <span className="text-gray-500 ml-1">({suggestion.count})</span>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Current Keywords */}
                        {activeKeywords.length > 0 && (
                            <div className="mb-6">
                                <div className="flex items-center justify-between mb-3">
                                    <h4 className="text-sm font-medium text-gray-700">Active Keywords ({activeKeywords.length}):</h4>
                                    <button
                                        onClick={clearAllKeywords}
                                        className="text-sm text-red-600 hover:text-red-800 underline"
                                    >
                                        Clear all
                                    </button>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {activeKeywords.map((keyword, index) => (
                                        <span
                                            key={index}
                                            className="inline-flex items-center px-3 py-2 rounded-lg text-sm bg-blue-100 text-blue-800"
                                        >
                                            {keyword}
                                            <button
                                                onClick={() => removeKeyword(keyword)}
                                                className="ml-2 hover:text-blue-600"
                                            >
                                                <X className="w-4 h-4" />
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Modal Actions */}
                        <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
                            <button
                                onClick={() => setShowFilterModal(false)}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default FiltersSection;